import './style.scss';

import { useEffect } from 'react';

import { CexPair } from 'entities/cex';
import { useTypedSelector } from 'store';
import { useFilters } from 'store/slices/filters/hooks/useFilters';
import { Bus } from 'tools';
import {
  EArbitrageFilters,
  ECexPairFilters,
  EDexPairFilters,
  EGeneralFilters,
  EProjectFilters,
  ESolPairFilters,
} from 'types/filters';
import { ICexPair, IDexPair } from 'types/pairs';
import { EExchange } from 'web3';

import FilterItem from '../FilterItem';

type CexPairLike = ICexPair | CexPair;

interface IFiltersListProps<V> {
  dexPair?: IDexPair;
  cexPair?: CexPairLike;
  solPair?: IDexPair;
  id: string;
  exchange?: V;
  general?: EGeneralFilters;
  arbitrage?: EArbitrageFilters;
  type?: V extends EExchange.cex
    ? ECexPairFilters
    : V extends EExchange.sol
      ? ESolPairFilters
      : EDexPairFilters;
  projectId?: number;
  projectType?: EProjectFilters;
}

function FiltersList<V extends EExchange>({
  dexPair: dexPairProp,
  cexPair: cexPairProp,
  solPair: solPairProp,
  id,
  exchange,
  type,
  general,
  arbitrage,
  projectId,
  projectType,
}: IFiltersListProps<V>) {
  const dexPair = dexPairProp ?? useTypedSelector(store => store.pairs.selectedDexPair);
  const cexPair = cexPairProp ?? useTypedSelector(store => store.pairs.selectedCexPair);
  const solPair = solPairProp ?? useTypedSelector(store => store.pairs.selectedSolPair);

  const { scheme, pairFilters, addFilter, updateFilter, deleteFilter } = useFilters({
    general,
    arbitrage,
    project: projectId && projectType ? { projectId, type: projectType } : undefined,
    cex:
      exchange === EExchange.cex && type
        ? { pair: cexPair ?? undefined, type: type as ECexPairFilters }
        : undefined,
    dex:
      exchange === EExchange.dex && type
        ? { pair: dexPair ?? undefined, type: type as EDexPairFilters }
        : undefined,
    sol:
      exchange === EExchange.sol && type
        ? { pair: solPair ?? undefined, type: type as ESolPairFilters }
        : undefined,
  });

  useEffect(() => {
    const handleAddFilter = () => {
      addFilter();
    };

    Bus.on(`${id}-FILTERS-ADD`, handleAddFilter);

    return () => {
      Bus.off(`${id}-FILTERS-ADD`, handleAddFilter);
    };
  }, [id, addFilter]);

  if (pairFilters.length === 0) return null;

  return (
    <div className="mm-filters-container scrollable">
      {scheme &&
        pairFilters.map((filter, idx) => (
          <FilterItem
            key={idx}
            scheme={scheme}
            filter={filter}
            filters={pairFilters}
            exchange={exchange}
            updateFilter={updateFilter}
            deleteFilter={deleteFilter}
            cexPair={cexPair}
          />
        ))}
    </div>
  );
}

export default FiltersList;
